/**
 * Theme derived from wagandroll.com.
 *
 * Palette:
 *   - Navbar/primary: steel blue  #3D6B8E
 *   - CTA/accent:     warm orange #F4945F
 *   - Heading text:   dark navy   #1B3A5C
 *   - Body text:      cool gray   #4A5B6E
 *   - Background:     clean white #FFFFFF / off-white #F7F9FB
 *
 * Typography:
 *   - Headings: "Poppins" (sans-serif) — modern, clean, and highly readable
 *   - Body:     "Inter" (sans-serif) — clean and highly legible
 */
const theme = {
  colors: {
    // Primary — steel blue navbar (wagandroll.com header)
    primary: {
      main: '#3D6B8E',
      light: '#5A89AC',
      dark: '#2A4F6E',
      contrast: '#FFFFFF'
    },

    // Secondary — muted sage/teal for supporting UI
    secondary: {
      main: '#6B9E8E',
      light: '#8FBFB0',
      dark: '#4E7D6E',
      contrast: '#FFFFFF'
    },

    // Accent — warm orange CTA (the "Solicitar cita" button)
    accent: {
      main: '#F4945F',
      light: '#F8B48A',
      dark: '#D4743F',
      hoverAccessible: '#E88D5A',
      onMain: '#1B3A5C',
      contrast: '#FFFFFF',
      // Kept for legacy uses in components
      coral: '#F4945F',
      sage: '#6B9E8E',
      warm: '#F7F9FB',
      lavender: '#B8C8D8'
    },

    // Neutral grays — cool-toned to complement the blue palette
    neutral: {
      50:  '#F7F9FB',
      100: '#EDF1F5',
      200: '#D8E2EC',
      300: '#B8C8D8',
      400: '#8AA3B8',
      500: '#658096',
      600: '#4A5B6E',
      700: '#324258',
      800: '#1B2D42',
      900: '#0D1A28'
    },

    // Semantic colors
    success: '#5A9E7A',
    warning: '#E8A83C',
    error:   '#C95B5B',
    info:    '#5A89AC',

    // Surfaces
    background: {
      default:  '#F7F9FB',  // off-white with a cool tint
      paper:    '#FFFFFF',
      elevated: '#EDF1F5'
    },

    text: {
      primary:   '#1B3A5C',  // dark navy — heading color from screenshot
      secondary: '#4A5B6E',  // cool gray body text
      disabled:  '#8AA3B8',
      hint:      '#B8C8D8'
    },

    border: {
      light: '#D8E2EC',
      main:  '#B8C8D8',
      dark:  '#8AA3B8'
    },

    overlay: {
      modal: 'rgba(13, 26, 40, 0.55)',
    },

    status: {
      success: {
        background: '#E8F8E8',
        border: '#7BC87A',
        text: '#2E7D32',
        solid: '#2E7D32',
      },
      warning: {
        background: '#FFFDE7',
        border: '#D4B84A',
        text: '#7A5E00',
        solid: '#D4B84A',
      },
      error: {
        background: '#FFE6E6',
        border: '#F2B4B4',
        text: '#C95B5B',
        solid: '#C95B5B',
      }
    }
  },

  typography: {
    fontFamily: {
      // Modern sans-serif for headings — clean and readable
      heading: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
      // Clean sans-serif for body / UI
      primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
      monospace: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace'
    },
    fontSize: {
      xxs: '10px', // micro labels: table headers, badges
      xs: '12px',
      sm: '14px',
      base: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '30px',
      '4xl': '36px',
      '5xl': '48px'
    },
    // Fluid display sizes for marketing headings (mobile → desktop)
    fluid: {
      display: 'clamp(2rem, 5vw, 3.5rem)',
      h1: 'clamp(1.75rem, 4vw, 2.6rem)',
      h2: 'clamp(1.75rem, 3vw, 2.5rem)',
      h3: 'clamp(1.5rem, 3vw, 2.1rem)'
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    },
    lineHeight: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.75
    }
  },
  
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
    '3xl': '64px'
  },
  
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px'
  },
  
  shadows: {
    sm: '0 1px 2px rgba(27, 58, 92, 0.06)',
    md: '0 2px 8px rgba(27, 58, 92, 0.10)',
    lg: '0 4px 16px rgba(27, 58, 92, 0.14)',
    xl: '0 8px 24px rgba(27, 58, 92, 0.18)'
  },
  
  transitions: {
    fast: '150ms ease-in-out',
    normal: '250ms ease-in-out',
    slow: '350ms ease-in-out'
  }
};

export default theme;
